import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import PickupRequestsService from "../../../../app/data/pickup-requests/pickupRequestsService";
import { BolFieldValue } from "../../../../app/data/pickup-requests/bolModels";
import Loading from "../../../../ui-components/loading/loading";
import DuplicatePickupCheck from "./duplicatePickupCheck";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "../utils/pickupInboxQueryOptions";
import {
  getEffectiveSenderEmail,
  resolveRequesterTeamFromSenderEmail,
} from "../utils/requesterCompanyUtils";
import { isXgsiEmailDomain } from "../utils/bolShipperEmailUtils";

const pickupRequestsService = PickupRequestsService.getInstance();

const fieldStr = (field?: BolFieldValue | null): string =>
  field?.value != null ? String(field.value) : "";

export interface DuplicatePickupCheckPanelProps {
  pickupRequestId: number;
  teamId?: string | null;
  senderEmail?: string;
  excludePickupNumber?: string;
  onPickupNumberClick?: (pickupNumber: string) => void;
  onInitialTeamIdResolved?: (teamId: string) => void;
}

const DuplicatePickupCheckPanel: React.FC<DuplicatePickupCheckPanelProps> = ({
  pickupRequestId,
  teamId,
  senderEmail,
  excludePickupNumber,
  onPickupNumberClick,
  onInitialTeamIdResolved,
}) => {
  const { data: snapshot, isLoading: isBolLoading, isFetching: isBolFetching } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_BOL_SNAPSHOT, pickupRequestId],
    queryFn: () => pickupRequestsService.getBolSnapshot(pickupRequestId),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const { data: pickupRequest, isLoading: isPickupLoading } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST, pickupRequestId],
    queryFn: () => pickupRequestsService.getPickupRequest(pickupRequestId),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const effectiveSenderEmail = useMemo(
    () =>
      getEffectiveSenderEmail(
        pickupRequest?.senderEmail,
        pickupRequest?.forwardedSenderEmail,
        senderEmail
      ),
    [pickupRequest?.senderEmail, pickupRequest?.forwardedSenderEmail, senderEmail]
  );

  const customerSenderEmail = useMemo(() => {
    const email = effectiveSenderEmail?.toLowerCase();
    if (!email || isXgsiEmailDomain(email)) return undefined;
    return email;
  }, [effectiveSenderEmail]);

  const { data: emailResolvedTeam } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_REQUESTER_TEAM_BY_EMAIL, pickupRequestId, customerSenderEmail],
    queryFn: () => resolveRequesterTeamFromSenderEmail(customerSenderEmail),
    enabled:
      !isPickupLoading &&
      !!pickupRequest &&
      !pickupRequest.senderTeamId &&
      !!customerSenderEmail,
    retry: false,
    staleTime: Infinity,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const autoResolvedTeamId =
    pickupRequest?.senderTeamId ?? emailResolvedTeam?.teamId ?? null;

  const effectiveTeamId = teamId?.trim() || autoResolvedTeamId;

  useEffect(() => {
    if (teamId?.trim() || !autoResolvedTeamId) return;
    onInitialTeamIdResolved?.(autoResolvedTeamId);
  }, [autoResolvedTeamId, onInitialTeamIdResolved, teamId]);

  const bolDoc = snapshot?.bol_document;
  const bolNumber = fieldStr(bolDoc?.bol);
  const shipperZipCode = fieldStr(bolDoc?.pickupFrom?.zip);
  const consigneeZipCode = fieldStr(bolDoc?.shipTo?.zip);

  const isBolPending = isBolLoading || isBolFetching;

  if (isBolPending) {
    return (
      <div className="xgs-pickup-inbox__duplicate-check">
        <Loading isLoading mix="xgs-pickup-inbox__duplicate-check__loading" />
      </div>
    );
  }

  if (!bolNumber.trim() || !shipperZipCode.trim() || !consigneeZipCode.trim()) {
    return null;
  }

  return (
    <DuplicatePickupCheck
      bolNumber={bolNumber}
      teamId={effectiveTeamId ?? undefined}
      shipperZipCode={shipperZipCode}
      consigneeZipCode={consigneeZipCode}
      excludePickupNumber={excludePickupNumber}
      onPickupNumberClick={onPickupNumberClick}
    />
  );
};

export default DuplicatePickupCheckPanel;
